/**
 * Pool membership editor — the authoring surface for `question.boardGrades`
 * (docs/FeatureDesigns/question-pools-and-entitlement/question-class-retirement-implplan.md, step E).
 *
 * One row per board, a checkbox per grade. What the author ticks IS what the
 * question serves: no resolver reinterprets it, and `class` is derived from it
 * at the write funnel rather than being a second thing to keep in step.
 *
 * Exam tracks (JEE/NEET) carry two independent notions and so get an extra
 * "board-wide" box:
 *   - the BARE key ('jee') is the entitlement — a JEE student holds the whole
 *     track, not one grade of it;
 *   - a GRADED key ('jee:11') is what makes the question reachable by anything
 *     that filters on grade.
 * A track question normally wants both, which is why they are separate boxes
 * rather than one control that guesses.
 *
 * Keys are the stored shape; the {board, grades[], boardWide} view is derived
 * for display only, via the same group/flatten pair the rest of the codebase
 * uses — so this editor cannot invent a key format of its own.
 */
import { useMemo } from 'react';
import {
    GRADE_OPTIONS,
    PROGRAMME_OPTIONS,
    groupBoardGradeKeys,
    toBoardGradeKeys,
    EXAM_TRACKS,
    type BoardGrade,
    type GradeType,
    type ProgrammeType,
} from '@padcanvas/shared';
import './BoardGradePicker.css';

interface Props {
    /** Stored keys ('cbse:10', 'jee'). */
    value: string[];
    onChange: (next: string[]) => void;
    disabled?: boolean;
}

export function BoardGradePicker({ value, onChange, disabled }: Props) {
    // An invalid stored key would throw out of groupBoardGradeKeys and take the
    // whole editor with it. Degrade to an empty view instead: an author can then
    // re-tick the pools, where a crash leaves them unable to edit the question
    // at all.
    const rows = useMemo<BoardGrade[]>(() => {
        try {
            return groupBoardGradeKeys(value ?? []);
        } catch {
            return [];
        }
    }, [value]);

    // Keyed by the board union, not `string`. Keying by string needs the toggles
    // to cast (`board as BoardGrade['board']`, `grade as never`), and those casts
    // hide a real signature mismatch that admin-console's tsc accepts and
    // student-app's rejects. Typing the whole chain — the map, `emit`, and both
    // toggles — removes the casts instead of widening them.
    const byBoard = new Map<ProgrammeType, BoardGrade>(rows.map(r => [r.board, r]));

    const emit = (next: Map<ProgrammeType, BoardGrade>): void => {
        onChange(toBoardGradeKeys([...next.values()]));
    };

    const toggleGrade = (board: ProgrammeType, grade: GradeType): void => {
        const next = new Map(byBoard);
        const row = next.get(board) ?? { board };
        const grades = new Set<GradeType>(row.grades ?? []);
        if (grades.has(grade)) grades.delete(grade);
        else grades.add(grade);
        const updated: BoardGrade = { ...row, grades: [...grades] };
        if (!updated.grades?.length) delete updated.grades;
        // A row with neither grades nor a board-wide key means the board is not
        // held at all — drop it rather than storing an empty shell.
        if (!updated.grades?.length && !updated.boardWide) next.delete(board);
        else next.set(board, updated);
        emit(next);
    };

    const toggleBoardWide = (board: ProgrammeType): void => {
        const next = new Map(byBoard);
        const row = next.get(board) ?? { board };
        const updated: BoardGrade = { ...row, boardWide: !row.boardWide };
        if (!updated.boardWide) delete updated.boardWide;
        if (!updated.grades?.length && !updated.boardWide) next.delete(board);
        else next.set(board, updated);
        emit(next);
    };

    return (
        <div className="bg-picker">
            <table className="bg-picker-grid">
                <thead>
                    <tr>
                        <th />
                        {GRADE_OPTIONS.map(g => <th key={g}>{g}</th>)}
                        <th className="bg-picker-wide-col">board-wide</th>
                    </tr>
                </thead>
                <tbody>
                    {PROGRAMME_OPTIONS.map(board => {
                        const row = byBoard.get(board);
                        const isTrack = EXAM_TRACKS.has(board);
                        return (
                            <tr key={board}>
                                <th scope="row">{board}</th>
                                {GRADE_OPTIONS.map(g => (
                                    <td key={g}>
                                        <input
                                            type="checkbox"
                                            aria-label={`${board} grade ${g}`}
                                            disabled={disabled}
                                            checked={!!row?.grades?.includes(g)}
                                            onChange={() => toggleGrade(board, g)}
                                        />
                                    </td>
                                ))}
                                <td className="bg-picker-wide-col">
                                    {/* Only tracks have a meaningful gradeless form. A school
                                        board with no grade names no pool at all. */}
                                    {isTrack ? (
                                        <input
                                            type="checkbox"
                                            aria-label={`${board} board-wide`}
                                            disabled={disabled}
                                            checked={!!row?.boardWide}
                                            onChange={() => toggleBoardWide(board)}
                                        />
                                    ) : <span className="muted">—</span>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <p className="bg-picker-hint muted">
                {value?.length
                    ? <>Serves: <code>{value.join(', ')}</code></>
                    : <>No pools selected — this question will not appear in any student’s library.</>}
            </p>
        </div>
    );
}
